/**
 * Combined hook for content comments.
 *
 * Returns query data + mutations + SSE callback placeholder.
 * Test contract: useContentComments(contentId, tenantId)
 * Returns: { isLoading, comments, createComment, resolveComment, onNewComment, commentsByBlock }
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contentFeedbackService } from '../services/contentFeedbackService';
import { supabase } from '../services/supabaseClient';
import type {
	CommentsGroupedByBlock,
	CommentsWithRepliesByBlock,
	ContentComment,
	CreateCommentRequest,
} from '../types/contentFeedback';

const QUERY_KEY = 'content-comments';

/**
 * Transform flat grouped comments into threaded structure.
 * Backend returns comments flat within each block group; this nests replies under parents.
 */
function buildThreaded(
	grouped: CommentsGroupedByBlock,
): CommentsWithRepliesByBlock {
	const result: CommentsWithRepliesByBlock = {};

	for (const [blockId, comments] of Object.entries(grouped)) {
		const roots: (ContentComment & { replies: ContentComment[] })[] = [];
		const replyMap = new Map<string, ContentComment[]>();

		// First pass: separate roots from replies
		for (const c of comments) {
			if (!c.parent_comment_id) {
				roots.push({ ...c, replies: [] });
			} else {
				const existing = replyMap.get(c.parent_comment_id) || [];
				existing.push(c);
				replyMap.set(c.parent_comment_id, existing);
			}
		}

		// Second pass: attach replies to roots
		for (const root of roots) {
			root.replies = replyMap.get(root.id) || [];
		}

		result[blockId] = roots;
	}

	return result;
}

export function useContentComments(
	contentId: string | null,
	tenantId: string,
	options?: { enabled?: boolean },
) {
	const queryClient = useQueryClient();
	const queryKey = [QUERY_KEY, contentId];

	// Fetch comments
	const {
		data: rawComments,
		isLoading,
	} = useQuery({
		queryKey,
		queryFn: () => contentFeedbackService.listComments(contentId!),
		enabled: !!contentId && (options?.enabled !== false),
		staleTime: 10_000,
		refetchInterval: 30_000,
	});

	// Build threaded structure
	const commentsByBlock = rawComments ? buildThreaded(rawComments) : {};
	const comments = commentsByBlock;

	// Create comment mutation
	const createMutation = useMutation({
		mutationFn: (data: CreateCommentRequest) =>
			contentFeedbackService.createComment(contentId!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	// Resolve comment mutation
	const resolveMutation = useMutation({
		mutationFn: (commentId: string) =>
			contentFeedbackService.resolveComment(contentId!, commentId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	// Create comment wrapper — auto-injects logged-in user info
	const createComment = useCallback(
		async (data: CreateCommentRequest) => {
			// Resolve user from Supabase session (avoids useAuth hook dependency)
			let authorId = 'system';
			let authorName = 'System';
			try {
				const { data: sessionData } = await supabase.auth.getSession();
				const user = sessionData?.session?.user;
				if (user) {
					authorId = user.id;
					authorName =
						user.user_metadata?.full_name ||
						user.user_metadata?.name ||
						user.email?.split('@')[0] ||
						'System';
				}
			} catch {
				// Fallback to defaults if auth unavailable
			}
			return createMutation.mutateAsync({
				author_id: authorId,
				author_name: authorName,
				author_role: 'internal',
				...data,
			});
		},
		[createMutation],
	);

	// Resolve comment wrapper
	const resolveComment = useCallback(
		async (commentId: string) => {
			return resolveMutation.mutateAsync(commentId);
		},
		[resolveMutation],
	);

	// SSE callback placeholder — will be wired to Redis pub/sub in Phase 2
	const onNewComment = useCallback(
		(_comment: ContentComment) => {
			queryClient.invalidateQueries({ queryKey });
		},
		[queryClient, queryKey],
	);

	return {
		isLoading,
		comments,
		commentsByBlock,
		createComment,
		resolveComment,
		onNewComment,
	};
}
